// Where Slowed runs. Pure: the background, the popup, the content script and
// the shortcuts all ask the same question of a URL.
import { isSafari } from "../platform";

export const YOUTUBE = "youtube";
export const YOUTUBE_MUSIC = "youtube-music";
export const SOUNDCLOUD = "soundcloud";
export const SPOTIFY = "spotify";
export const BANDCAMP = "bandcamp";
export const APPLE_MUSIC = "apple-music";

export const SOUNDCLOUD_URL = "https://soundcloud.com/";
export const SPOTIFY_URL = "https://open.spotify.com/";
export const BANDCAMP_URL = "https://bandcamp.com/";
export const APPLE_MUSIC_URL = "https://music.apple.com/";

// SoundCloud, Spotify, Bandcamp and Apple Music are optional host
// permissions, asked for the first time Slowed is opened there. Listing them
// under host_permissions instead would show every existing install a new
// permission warning on update, and Chrome switches an extension off until
// that warning is accepted.
//
// Mobile Safari is sent to m.soundcloud.com. It is added on Safari only, for
// the same reason: on Chrome it would be one more warning on update. Keep it
// in step with safari/manifest.js.
export const MOBILE_SOUNDCLOUD_PAGES = ["*://m.soundcloud.com/*"];
export const SOUNDCLOUD_PAGES = [
  "*://soundcloud.com/*",
  ...(isSafari() ? MOBILE_SOUNDCLOUD_PAGES : []),
];
// w.soundcloud.com is SoundCloud's embedded player, which Slowed Studio
// (studio.slowed.app) plays pasted SoundCloud links in.
export const SOUNDCLOUD_EMBED_ORIGINS = ["*://w.soundcloud.com/*"];
export const SOUNDCLOUD_EMBEDS = ["*://w.soundcloud.com/player/*"];
// What is asked for: one prompt covers the site and the embedded player. Each
// half works on its own grant, so an install allowed on soundcloud.com before
// the player was added keeps working there.
export const SOUNDCLOUD_ORIGINS = [
  ...SOUNDCLOUD_PAGES,
  ...SOUNDCLOUD_EMBED_ORIGINS,
];

// Spotify's web player (open.spotify.com) and its embedded player, which
// Studio plays pasted Spotify links in, share one host: one grant covers
// both. Spotify plays on a phone in its app, not on the web, so there is no
// mobile host to add on Safari.
export const SPOTIFY_PAGES = ["*://open.spotify.com/*"];
export const SPOTIFY_EMBED_ORIGINS = SPOTIFY_PAGES;
export const SPOTIFY_EMBEDS = ["*://open.spotify.com/embed/*"];
export const SPOTIFY_ORIGINS = SPOTIFY_PAGES;

// Bandcamp is every artist's own subdomain (mitski.bandcamp.com) and the
// front page; its embedded player, which Studio plays pasted Bandcamp links
// in, lives under bandcamp.com/EmbeddedPlayer/. One grant covers all of it.
// An artist's own domain (a custom domain on Bandcamp) is not covered.
export const BANDCAMP_PAGES = ["*://*.bandcamp.com/*"];
export const BANDCAMP_EMBED_ORIGINS = BANDCAMP_PAGES;
export const BANDCAMP_EMBEDS = ["*://bandcamp.com/EmbeddedPlayer/*"];
export const BANDCAMP_ORIGINS = BANDCAMP_PAGES;

// Apple Music's web player (music.apple.com) and its embedded player
// (embed.music.apple.com, for Studio) are two hosts, asked for in one
// prompt, as SoundCloud's are.
export const APPLE_MUSIC_PAGES = ["*://music.apple.com/*"];
export const APPLE_MUSIC_EMBED_ORIGINS = ["*://embed.music.apple.com/*"];
export const APPLE_MUSIC_EMBEDS = APPLE_MUSIC_EMBED_ORIGINS;
export const APPLE_MUSIC_ORIGINS = [
  ...APPLE_MUSIC_PAGES,
  ...APPLE_MUSIC_EMBED_ORIGINS,
];

// Whether this build carries Spotify at all: every build does, as Spotify
// (Beta), off until the listener switches it on (./spotify-beta.js).
// webpack.common.js sets SLOWED_SPOTIFY per build (0 builds one without);
// a test run, with no setting, has it. Without it, open.spotify.com is a
// site Slowed does not know: nothing registered, asked for or offered.
export function isSpotifyEnabled() {
  const flag = process.env.SLOWED_SPOTIFY;
  return flag === undefined || flag === true || flag === "true" || flag === "1";
}

// A site's id as analytics event names take it (apple-music_access_granted
// would be refused for its dash).
export function siteEventKey(site) {
  return String(site || "").replace(/-/g, "_");
}

// The sites that run on a permission the listener grants (sites/access.js).
export const OPTIONAL_SITES = [
  SOUNDCLOUD,
  ...(isSpotifyEnabled() ? [SPOTIFY] : []),
  BANDCAMP,
  APPLE_MUSIC,
];

const SOUNDCLOUD_HOSTS = new Set([
  "soundcloud.com",
  "www.soundcloud.com",
  ...(isSafari() ? ["m.soundcloud.com"] : []),
]);
const SOUNDCLOUD_EMBED_HOST = "w.soundcloud.com";
const SPOTIFY_HOST = "open.spotify.com";
const SPOTIFY_EMBED_PATH = "/embed/";
const BANDCAMP_HOST = "bandcamp.com";
const BANDCAMP_EMBED_PATH = "/EmbeddedPlayer/";
const APPLE_MUSIC_HOST = "music.apple.com";
const APPLE_MUSIC_EMBED_HOST = "embed.music.apple.com";

function isBandcampHost(hostname) {
  return hostname === BANDCAMP_HOST || hostname.endsWith(`.${BANDCAMP_HOST}`);
}

export function getSiteId(url) {
  let hostname;
  let pathname;
  try {
    ({ hostname, pathname } = new URL(url));
  } catch (error) {
    return null;
  }

  if (hostname === "music.youtube.com") return YOUTUBE_MUSIC;
  // Includes m.youtube.com, where Mobile Safari is sent (the Safari build's
  // manifest runs there; Chrome's does not, see safari/manifest.js).
  if (hostname === "youtube.com" || hostname.endsWith(".youtube.com")) {
    return YOUTUBE;
  }
  if (SOUNDCLOUD_HOSTS.has(hostname)) return SOUNDCLOUD;
  // The embedded player is not the site: a tab showing it has no panel.
  if (hostname === SPOTIFY_HOST && isSpotifyEnabled()) {
    return pathname.startsWith(SPOTIFY_EMBED_PATH) ? null : SPOTIFY;
  }
  if (isBandcampHost(hostname)) {
    return hostname === BANDCAMP_HOST && pathname.startsWith(BANDCAMP_EMBED_PATH)
      ? null
      : BANDCAMP;
  }
  if (hostname === APPLE_MUSIC_HOST) return APPLE_MUSIC;
  return null;
}

export function isSoundCloudUrl(url) {
  return getSiteId(url) === SOUNDCLOUD;
}

export function isSpotifyUrl(url) {
  return getSiteId(url) === SPOTIFY;
}

export function isBandcampUrl(url) {
  return getSiteId(url) === BANDCAMP;
}

export function isAppleMusicUrl(url) {
  return getSiteId(url) === APPLE_MUSIC;
}

// A site that runs on a permission the listener grants.
export function isOptionalSiteUrl(url) {
  return OPTIONAL_SITES.includes(getSiteId(url));
}

// SoundCloud's embedded player. Not a site of its own (a tab showing it has
// no panel): the content script treats it as SoundCloud inside Slowed Studio.
export function isSoundCloudEmbedUrl(url) {
  try {
    const { hostname, pathname } = new URL(url);
    return hostname === SOUNDCLOUD_EMBED_HOST && pathname.startsWith("/player");
  } catch (error) {
    return false;
  }
}

// Spotify's embedded player, the same way.
export function isSpotifyEmbedUrl(url) {
  if (!isSpotifyEnabled()) return false;
  try {
    const { hostname, pathname } = new URL(url);
    return hostname === SPOTIFY_HOST && pathname.startsWith(SPOTIFY_EMBED_PATH);
  } catch (error) {
    return false;
  }
}

// Bandcamp's embedded player, the same way.
export function isBandcampEmbedUrl(url) {
  try {
    const { hostname, pathname } = new URL(url);
    return hostname === BANDCAMP_HOST && pathname.startsWith(BANDCAMP_EMBED_PATH);
  } catch (error) {
    return false;
  }
}

// Apple Music's embedded player, the same way.
export function isAppleMusicEmbedUrl(url) {
  try {
    return new URL(url).hostname === APPLE_MUSIC_EMBED_HOST;
  } catch (error) {
    return false;
  }
}

// A site's embedded player: Slowed runs in one only when it sits directly in
// a Studio page (sites/studio.js).
export function isEmbedUrl(url) {
  return (
    isSoundCloudEmbedUrl(url) ||
    isSpotifyEmbedUrl(url) ||
    isBandcampEmbedUrl(url) ||
    isAppleMusicEmbedUrl(url)
  );
}
